import { useState } from 'react';
import { UsersRound } from 'lucide-react';
import type { ConversationDetail } from '../api/conversations';
import { ComplianceNotApplicable, ComplianceSatisfied } from './ComplianceNotice';
import { OverflowChip, ParticipantChip, ParticipantLegend } from './ParticipantChip';
import { ParticipantsPanel } from './ParticipantsPanel';

/**
 * Who is in the thread, and who else can read it.
 *
 * **One definition, used by every thread screen.** It was duplicated across the member's screen and
 * the parent's for exactly as long as it took to change it — and a second rendering of this
 * particular strip is the kind that quietly omits somebody, which rule 7 forbids outright.
 *
 * Bounded. A whole-unit thread in the seeded troop has seventy participants and forty-six guardian
 * readers, and the guardian line used to render every one of those names into a single caption
 * sentence nine hundred and ninety-seven characters long. Counts stay on screen — a youth about to
 * type should register that adults are monitoring and parents can read without tapping anything —
 * and the enumeration moves into {@link ParticipantsPanel}.
 */
const VISIBLE_CHIPS = 8;

export function ParticipantStrip({
  detail,
  unitId,
  onChanged,
}: {
  detail: ConversationDetail | null;
  /**
   * Which unit's routes to call when somebody edits who is in the thread (TROOP-178). Undefined on
   * the guardian's door: a parent holds no membership, so `/units/` 404s them and there is no
   * office for them to hold either — the panel renders no controls rather than controls that
   * would be refused.
   */
  unitId?: string;
  /** Re-reads the thread after an edit, so the strip and the panel are never one change behind. */
  onChanged?: () => void;
}) {
  const [panelOpen, setPanelOpen] = useState(false);

  const active = detail?.participants.filter((p) => p.mode === 'Active') ?? [];
  const monitors = detail?.participants.filter((p) => p.mode === 'Monitor') ?? [];
  const readers = detail?.guardianReaders ?? [];

  const everyone = active.length + monitors.length + readers.length;

  return (
    <div className="flex flex-col" style={{ paddingBlock: 'var(--space-5)', gap: 'var(--space-4)' }}>
      <div className="flex items-center justify-between gap-4">
        <h2 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          In this thread
        </h2>

        {detail?.complianceState === 'Satisfied' && (
          <ComplianceSatisfied adultCount={detail.adultCount} />
        )}
        {detail?.complianceState === 'NotApplicable' && <ComplianceNotApplicable />}
      </div>

      {/* Still conditional, but no longer for the reason it was. It used to empty on every
          roster-scoped thread, because the projection reported all ninety-three derived people as
          monitors (TROOP-61); the aggregate now reports the capacity it actually derived, so a
          whole-unit thread fills this row. What remains is the honest case — an explicit thread of
          adults where everyone present was added to oversee it — and an "In this thread" heading
          over nothing still reads as a broken screen. */}
      {active.length > 0 && <ChipRow people={active} onOverflow={() => setPanelOpen(true)} />}

      {monitors.length > 0 && (
        <div>
          <p
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-2)' }}
          >
            Monitored by
          </p>
          <ChipRow people={monitors} onOverflow={() => setPanelOpen(true)} />
        </div>
      )}

      <ParticipantLegend />

      {/* Disclosed, never silent, and no longer a wall of names. Guardians can read any thread
          containing their youth, everyone in it is told so, and who they are is one tap away. */}
      {readers.length > 0 && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {readers.length === 1
            ? 'One parent or guardian of the young people here can also read this thread.'
            : `${readers.length} parents and guardians of the young people here can also read this thread.`}
        </p>
      )}

      {detail && everyone > 0 && (
        <p style={{ margin: 0 }}>
          <button
            type="button"
            className="link-button inline-flex items-center gap-2"
            onClick={() => setPanelOpen(true)}
            style={{ minHeight: 'var(--tap-min)' }}
          >
            <UsersRound size={15} aria-hidden="true" />
            See everyone &middot; <span className="tabular">{everyone}</span>
          </button>
        </p>
      )}

      {panelOpen && detail && (
        <ParticipantsPanel
          detail={detail}
          unitId={unitId}
          onChanged={onChanged}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </div>
  );
}

function ChipRow({
  people,
  onOverflow,
}: {
  people: ConversationDetail['participants'];
  onOverflow: () => void;
}) {
  const shown = people.slice(0, VISIBLE_CHIPS);
  const hidden = people.length - shown.length;

  return (
    <div className="flex flex-wrap items-center" style={{ gap: '7px' }}>
      {shown.map((p) => (
        <ParticipantChip
          key={p.personId}
          participant={{
            id: p.personId,
            name: p.displayName,
            classification: p.classification === 'Youth' ? 'youth' : 'adult',
          }}
        />
      ))}

      {hidden > 0 && <OverflowChip count={hidden} onClick={onOverflow} />}
    </div>
  );
}
